import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, Share2, FileText, DollarSign, Users, Calendar, AlertCircle, ExternalLink } from 'lucide-react';
import PDFViewerWithHighlights from './PDFViewerWithHighlights_SIMPLE';
import HTMLViewerWithHighlights from './HTMLViewerWithHighlights';
import { getPDFUrl, getCitations, getDocumentTitle } from '../utils/documentUtils';
import { DATA_API_URL } from '../lib/api';
import RoleTailoredDocumentCard from './RoleTailoredDocumentCard';

interface ReportDetailPageProps {
  document: any; // This is the PolicyUpdate object from dashboard
  onBack: () => void;
  source?: 'live' | 'archive';
  prefetchedDocument?: any;
}

export default function ReportDetailPage({ document: policyUpdate, onBack, source = 'live', prefetchedDocument }: ReportDetailPageProps) {
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [showHtmlViewer, setShowHtmlViewer] = useState(false);
  const [viewerData, setViewerData] = useState<any>(null);
  const [actualDocument, setActualDocument] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch the actual document from API using the ID
    const fetchActualDocument = async () => {
      try {
        const API_BASE_URL = 'https://inferviaapi-production.up.railway.app';
        // Try direct endpoint first
        let doc = null;
        try {
          const direct = await fetch(`${API_BASE_URL}/api/changes/${policyUpdate.id}`);
          if (direct.ok) {
            doc = await direct.json();
          }
        } catch (err) {
          console.warn('Direct change fetch failed, falling back to recent list', err);
        }

        if (!doc) {
          const response = await fetch(`${API_BASE_URL}/api/changes/recent?limit=200`);
          if (response.ok) {
            const docs = await response.json();
            doc = docs.find((d: any) => d.document_id.toString() === policyUpdate.id);
          }
        }

        setActualDocument(doc || null);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load document:', error);
        setLoading(false);
      }
    };

    if (prefetchedDocument && source === 'archive') {
      setActualDocument(prefetchedDocument);
      setLoading(false);
      return;
    }

    if (policyUpdate?.id && source === 'live') {
      fetchActualDocument();
    }
  }, [policyUpdate, prefetchedDocument, source]);

  if (!policyUpdate) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorIcon}>📄</div>
        <h2 style={styles.errorTitle}>No Document Selected</h2>
        <button onClick={onBack} style={styles.backButtonError}>
          <ArrowLeft size={16} />
          <span>Back to Dashboard</span>
        </button>
      </div>
    );
  }

  const documentId = parseInt(policyUpdate.id, 10);
  const resolvedDoc = actualDocument || prefetchedDocument || policyUpdate?.originalData || policyUpdate;
  const pdfUrl = getPDFUrl(resolvedDoc);

  const handleViewDocument = async () => {
    if (!resolvedDoc) return;
    try {
      console.group('📄 Opening Document Viewer');
      console.log('Document:', resolvedDoc);
      console.log('PDF URL:', pdfUrl);

      if (!pdfUrl) {
        console.error('❌ No PDF URL found');
        alert('PDF not available for this document');
        console.groupEnd();
        return;
      }

      let fileType: 'pdf' | 'html' = 'pdf';

      // Validate PDF is reachable and is a PDF
      try {
        const headResp = await fetch(pdfUrl, { method: 'HEAD' });
        if (!headResp.ok) {
          throw new Error(`PDF returned ${headResp.status}`);
        }
        const contentType = headResp.headers.get('content-type');
        console.log('Content-Type:', contentType);

        // If it's HTML, switch to HTML viewer
        if (contentType && contentType.includes('html')) {
          console.log('⚠️ Document is HTML - will use HTML viewer');
          fileType = 'html';
        }

        // If not PDF and not HTML, show error
        if (contentType && !contentType.includes('pdf') && fileType !== 'html') {
          throw new Error(`Invalid PDF content-type: ${contentType}`);
        }
      } catch (err) {
        console.warn('⚠️ Could not check content type, will infer from extension');
      }

      console.log('📋 Fetching citations...');
      const citations = await getCitations(resolvedDoc, DATA_API_URL);
      console.log('✅ Citations loaded:', citations.length);

      const title = getDocumentTitle(resolvedDoc);
      console.log('✅ Title:', title);

      // Enrich document with PDF and citations for the viewer
      const docWithData = {
        ...resolvedDoc,
        pdf_url: pdfUrl,
        source_citations: citations,
      };
      setActualDocument(docWithData);
      setViewerData({
        url: pdfUrl,
        title,
        citations,
        document: docWithData,
      });
      console.groupEnd();

      if (pdfUrl.endsWith('.html')) fileType = 'html';
      if (pdfUrl.endsWith('.pdf')) fileType = 'pdf';

      if (fileType === 'html') {
        setShowHtmlViewer(true);
      } else {
        setShowPdfViewer(true);
      }
    } catch (error) {
      console.error('❌ Error opening document viewer:', error);
      alert('Failed to open document viewer');
    }
  };

  if (showPdfViewer && viewerData) {
    return (
      <PDFViewerWithHighlights 
        documentId={documentId}
        documentData={{ ...viewerData.document, pdf_url: viewerData.url }}
        source={source}
        onClose={() => setShowPdfViewer(false)}
      />
    );
  }

  if (showHtmlViewer && viewerData) {
    return (
      <HTMLViewerWithHighlights
        htmlUrl={viewerData.url}
        title={viewerData.title}
        citations={viewerData.citations}
        onClose={() => setShowHtmlViewer(false)}
      />
    );
  }

  // Use data from policyUpdate (transformed dashboard data)
  const analysis =
    resolvedDoc?.simplified_analysis ||
    policyUpdate?.originalData?.simplified_analysis ||
    policyUpdate?.simplified_analysis ||
    {};
  const citations =
    resolvedDoc?.source_citations ||
    analysis?.source_citations ||
    policyUpdate?.source_citations ||
    [];

  // Debug logs for citation flow
  console.log('=== CITATION DEBUG ===');
  console.log('1. Resolved document:', resolvedDoc);
  console.log('2. resolvedDoc.source_citations:', resolvedDoc?.source_citations);
  console.log('3. resolvedDoc.simplified_analysis:', resolvedDoc?.simplified_analysis);
  console.log('4. resolvedDoc.simplified_analysis.source_citations:', resolvedDoc?.simplified_analysis?.source_citations);
  console.log('5. Final citations array:', citations);
  console.log('6. Citations length:', citations?.length);
  console.log('=====================');

  const currentRole = localStorage.getItem('userRole') || '';
  const getRoleLabel = () => {
    const roleOptions = [
      { value: 'coo', label: 'COO / Chief Operating Officer' },
      { value: 'compliance', label: 'Compliance Officer' },
      { value: 'regulatory', label: 'Regulatory Affairs' },
      { value: 'billing', label: 'Billing / Revenue Cycle' },
      { value: 'clinical', label: 'Clinical Operations' },
      { value: 'quality', label: 'Quality Assurance' },
      { value: 'legal', label: 'Legal / Risk Management' },
    ];
    const role = roleOptions.find(r => r.value === currentRole);
    return role ? role.label : 'All roles';
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <button onClick={onBack} style={styles.backButton}>
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </button>

          <div style={styles.headerActions}>
            <button style={styles.actionButton}>
              <Download size={18} />
              <span>Export Report</span>
            </button>
            <button style={styles.actionButton}>
              <Share2 size={18} />
              <span>Schedule Review</span>
            </button>
          </div>
        </div>

        <div style={styles.metadata}>
          {currentRole && (
            <div style={styles.roleIndicator}>
              <Users size={14} />
              <span>Viewing as: {getRoleLabel()}</span>
            </div>
          )}
          <div style={styles.badge}>{policyUpdate.department || 'CMS'}</div>
          <div style={styles.badge}>
            {getPriorityBadge(policyUpdate.urgency)}
          </div>
          <div style={styles.metaText}>
            {policyUpdate.timestamp}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.content}>
        {/* Title Section */}
        <div style={styles.titleSection}>
          <h1 style={styles.title}>{policyUpdate.title}</h1>
          
          {/* One-sentence summary as subheading */}
          {analysis.one_sentence_summary && (
            <p style={styles.oneSentenceSummary}>
              {analysis.one_sentence_summary}
            </p>
          )}

          <div style={styles.docMeta}>
            <div style={styles.metaItem}>
              <Calendar size={16} />
              <span>EFFECTIVE DATE</span>
              <strong>{policyUpdate.effectiveDate}</strong>
            </div>
            <div style={styles.metaItem}>
              <AlertCircle size={16} />
              <span>STATUS</span>
              <strong>{policyUpdate.status}</strong>
            </div>
            <div style={styles.metaItem}>
              <FileText size={16} />
              <span>IMPACT</span>
              <strong>{policyUpdate.urgency}</strong>
            </div>
            <div style={styles.metaItem}>
              <Users size={16} />
              <span>DEPARTMENTS</span>
              <strong>{policyUpdate.affectedDepartments?.length || 0} affected</strong>
            </div>
          </div>

          {/* View Source Evidence Button - Opens in new tab if PDF blocked */}
          <div style={styles.sourceButton}>
            {pdfUrl ? (
              <>
                <button
                  onClick={handleViewDocument}
                  style={styles.viewSourceButton}
                >
                  <FileText size={20} />
                  <span>View Source Evidence</span>
                </button>
                <button
                  onClick={() => window.open(
                    pdfUrl,
                    '_blank'
                  )}
                  style={styles.openNewTabButton}
                >
                  <ExternalLink size={18} />
                  <span>Open PDF in New Tab</span>
                </button>
              </>
            ) : (
              <div style={styles.loadingPdf}>
                {loading ? 'Loading PDF link...' : 'PDF not available'}
              </div>
            )}
          </div>
        </div>

        {/* Role-Tailored Information Card */}
        {resolvedDoc && resolvedDoc.role_tailored_content && (
          <div style={{ marginBottom: '24px' }}>
            <RoleTailoredDocumentCard
              document={{
                id: policyUpdate.id,
                title: resolvedDoc.title,
                priority: resolvedDoc.priority || 'medium',
                source: resolvedDoc.source,
                source_link: resolvedDoc.url || resolvedDoc.document_info?.pdf_url,
                date: resolvedDoc.date,
                role_tailored_content: resolvedDoc.role_tailored_content,
                simplified_analysis: resolvedDoc.simplified_analysis,
                affected_departments: resolvedDoc.affected_departments,
                url: resolvedDoc.url || resolvedDoc.document_info?.pdf_url
              }}
              viewMode="detail"
            />
          </div>
        )}

        {/* Sections */}
        <div style={styles.sections}>
          {/* Paragraph Summary Block */}
          {analysis.paragraph_summary && (
            <Section title="Summary">
              <p style={styles.paragraphSummary}>{analysis.paragraph_summary}</p>
            </Section>
          )}

          {/* Detailed Changes */}
          <Section title="Detailed Changes">
            <div style={styles.changesList}>
              {/* Use detailed_changes from backend if available, otherwise fall back to key_requirements */}
              {(analysis.detailed_changes && analysis.detailed_changes.length > 0 ? 
                analysis.detailed_changes : 
                policyUpdate.detailedChanges
              )?.map((change: string, idx: number) => (
                <div key={idx} style={styles.changeItem}>
                  <div style={styles.bulletPoint}></div>
                  <p>{change}</p>
                </div>
              )) || <p>No detailed changes specified</p>}
            </div>

            <div style={styles.affectedDepts}>
              <h4 style={styles.subsectionTitle}>
                <Users size={16} />
                Affected Departments
              </h4>
              <div style={styles.deptTags}>
                {policyUpdate.affectedDepartments?.map((dept: string, idx: number) => (
                  <span key={idx} style={styles.deptTag}>{dept}</span>
                )) || <span>None specified</span>}
              </div>
            </div>
          </Section>

          {/* Action Items - Hidden when role-tailored content exists (shown in card above) */}
          {!resolvedDoc?.role_tailored_content && (
            <Section title="Action Items">
              {policyUpdate.actionItems?.map((item: any, idx: number) => (
                <div key={idx} style={styles.actionItem}>
                  <div style={styles.actionHeader}>
                    <span style={getPriorityStyle(item.priority)}>{item.priority}</span>
                    <span style={styles.actionOwner}>{item.assignedDepartment}</span>
                  </div>
                  <div style={styles.actionDescription}>
                    {item.description}
                  </div>
                  <div style={styles.actionTimeline}>
                    <Calendar size={14} />
                    <span>Due: {item.dueDate}</span>
                  </div>
                </div>
              )) || <p>No action items specified</p>}

              <div style={styles.financialImpact}>
                <div style={styles.impactHeader}>
                  <DollarSign size={18} />
                  <span>Financial Impact</span>
                </div>
                <div style={styles.impactDetails}>
                  <div style={styles.impactRow}>
                    <span>Estimated Cost:</span>
                    <strong>{policyUpdate.financialImpact}</strong>
                  </div>
                  <div style={styles.impactRow}>
                    <span>Impact Description:</span>
                    <strong>{policyUpdate.impact}</strong>
                  </div>
                </div>
              </div>
            </Section>
          )}

          {/* Compliance Checklist - Hidden when role-tailored content exists (shown in card above) */}
          {!resolvedDoc?.role_tailored_content && (
            <Section title="Compliance Checklist">
              {policyUpdate.complianceChecklist?.map((item: any, idx: number) => (
                <div key={idx} style={styles.checklistItem}>
                  <input type="checkbox" checked={item.completed} style={styles.checkbox} readOnly />
                  <div style={styles.checklistContent}>
                    <p style={styles.checklistText}>{item.task}</p>
                    <span style={styles.checklistDate}>
                      Due: {item.dueDate}
                    </span>
                  </div>
                </div>
              )) || <p>No checklist items available</p>}
            </Section>
          )}

          {/* Source Documents */}
          <Section title="Source Documents">
            <div style={styles.sourceDoc}>
              <FileText size={20} color="#3b82f6" />
              <div style={styles.sourceDocInfo}>
                <h4 style={styles.sourceDocTitle}>{policyUpdate.title}</h4>
                <p style={styles.sourceDocMeta}>
                  {citations.length} citations • {policyUpdate.department || 'CMS'}
                </p>
              </div>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <div style={styles.sectionContent}>{children}</div>
    </div>
  );
}

function getPriorityBadge(priority?: string): string {
  const map: Record<string, string> = {
    High: 'High Priority',
    Medium: 'Medium Priority',
    Low: 'Low Priority'
  };
  return map[priority || 'Medium'] || 'Medium Priority';
}

function getPriorityStyle(priority: string): React.CSSProperties {
  const styles: Record<string, React.CSSProperties> = {
    High: { backgroundColor: '#fee2e2', color: '#dc2626', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    Medium: { backgroundColor: '#fef3c7', color: '#d97706', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    Low: { backgroundColor: '#dbeafe', color: '#2563eb', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }
  };
  return styles[priority] || styles.Medium;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
  },
  header: {
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0',
    padding: '24px 40px',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#475569',
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#475569',
  },
  metadata: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  roleIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 14px',
    backgroundColor: '#3b82f6',
    color: 'white',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600',
    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
  },
  badge: {
    padding: '4px 12px',
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  metaText: {
    fontSize: '14px',
    color: '#64748b',
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px',
  },
  titleSection: {
    backgroundColor: 'white',
    padding: '32px',
    borderRadius: '12px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  title: {
    margin: '0 0 16px 0',
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    lineHeight: '1.3',
  },
  oneSentenceSummary: {
    margin: '0 0 24px 0',
    fontSize: '16px',
    color: '#475569',
    lineHeight: '1.6',
    fontWeight: '500',
    fontStyle: 'italic',
  },
  subtitle: {
    margin: '0 0 24px 0',
    fontSize: '15px',
    color: '#64748b',
    lineHeight: '1.6',
  },
  docMeta: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '20px',
    padding: '20px 0',
    borderTop: '1px solid #e2e8f0',
    borderBottom: '1px solid #e2e8f0',
    marginBottom: '24px',
  },
  metaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '13px',
    color: '#64748b',
  },
  sourceButton: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    paddingTop: '16px',
  },
  viewSourceButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 32px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
  },
  openNewTabButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: 'white',
    color: '#3b82f6',
    border: '2px solid #3b82f6',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  loadingPdf: {
    padding: '12px 24px',
    color: '#64748b',
    fontSize: '14px',
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  section: {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
  },
  sectionContent: {
    fontSize: '14px',
    color: '#475569',
  },
  paragraphSummary: {
    fontSize: '15px',
    color: '#1e293b',
    lineHeight: '1.7',
    margin: 0,
  },
  changesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '24px',
  },
  changeItem: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
  },
  bulletPoint: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    marginTop: '8px',
    flexShrink: 0,
  },
  affectedDepts: {
    marginTop: '20px',
    paddingTop: '20px',
    borderTop: '1px solid #e2e8f0',
  },
  subsectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '0 0 12px 0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
  },
  deptTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  deptTag: {
    padding: '6px 12px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
  },
  actionItem: {
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    marginBottom: '12px',
    border: '1px solid #e2e8f0',
  },
  actionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  actionOwner: {
    fontSize: '13px',
    color: '#64748b',
    fontWeight: '500',
  },
  actionDescription: {
    fontSize: '14px',
    color: '#1e293b',
    marginBottom: '8px',
    lineHeight: '1.5',
  },
  actionTimeline: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#64748b',
  },
  financialImpact: {
    marginTop: '20px',
    padding: '16px',
    backgroundColor: '#fef3c7',
    borderRadius: '8px',
    border: '1px solid #fbbf24',
  },
  impactHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#78350f',
  },
  impactDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  impactRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: '#78350f',
  },
  checklistItem: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    borderBottom: '1px solid #e2e8f0',
  },
  checkbox: {
    marginTop: '4px',
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  checklistContent: {
    flex: 1,
  },
  checklistText: {
    margin: '0 0 4px 0',
    fontSize: '14px',
    color: '#1e293b',
  },
  checklistDate: {
    fontSize: '12px',
    color: '#64748b',
  },
  sourceDoc: {
    display: 'flex',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  sourceDocInfo: {
    flex: 1,
  },
  sourceDocTitle: {
    margin: '0 0 4px 0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
  },
  sourceDocMeta: {
    margin: 0,
    fontSize: '13px',
    color: '#64748b',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    gap: '16px',
    padding: '40px',
  },
  errorIcon: {
    fontSize: '48px',
  },
  errorTitle: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
  },
  backButtonError: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: 'white',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
};
